import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { scrollToHomeSection } from '../../navigation/homeSections';
import { buildWavePalette, getRouteAccent } from '../../transition';
import './Footer.css';

const hexToRgba = (hex, alpha) => {
  let h = String(hex || '').replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return `rgba(124, 92, 255, ${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const footerAccentStyle = (accent) => {
  if (!accent) return undefined;
  const palette = buildWavePalette(accent);
  return {
    '--accent-1': palette[0],
    '--accent-2': accent,
    '--accent-3': palette[2],
    '--accent-glow': hexToRgba(accent, 0.45),
    '--footer-accent-border': hexToRgba(accent, 0.55),
  };
};

const EMAIL = 'kgfuller@yahoo.com';
const COPY_LABEL = 'Copy email';
const COPIED_LABEL = 'Copied';

/** Copyright year + booking window derived from "today" so neither needs a manual bump. */
const getFooterDates = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0–11

  // Nov–Dec: book into early next year; otherwise stay in the current year.
  if (month >= 10) {
    return { year, workWindow: `early ${year + 1}` };
  }
  if (month >= 6) {
    return { year, workWindow: `late ${year}` };
  }
  if (month >= 3) {
    return { year, workWindow: `mid ${year}` };
  }
  return { year, workWindow: `early ${year}` };
};

const SITE_LINKS = [
  { label: 'Work', sectionId: 'projects' },
  { label: 'Services', sectionId: 'services' },
  { label: 'About', sectionId: 'about' },
  { label: 'Contact', sectionId: 'contact' },
];

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { year: YEAR, workWindow } = getFooterDates();
  const [tooltipText, setTooltipText] = useState(COPY_LABEL);
  const [justCopied, setJustCopied] = useState(false);
  const resetTimerRef = useRef(0);

  useEffect(() => () => window.clearTimeout(resetTimerRef.current), []);

  const handleSectionNav = (e, sectionId) => {
    e.preventDefault();
    if (location.pathname === '/') {
      scrollToHomeSection(sectionId, { behavior: 'smooth' });
      return;
    }
    navigate('/', { state: { scrollTo: sectionId } });
  };

  const handleCopyEmail = async () => {
    window.clearTimeout(resetTimerRef.current);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(EMAIL);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = EMAIL;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
      setTooltipText(COPIED_LABEL);
      setJustCopied(true);
    } catch {
      setTooltipText('Copy failed');
      setJustCopied(true);
    }
    resetTimerRef.current = window.setTimeout(() => {
      setJustCopied(false);
      setTooltipText(COPY_LABEL);
    }, 1600);
  };

  const projectAccent = getRouteAccent(location.pathname);

  return (
    <footer
      className={`site-footer${projectAccent ? ' site-footer--project' : ''}`}
      style={footerAccentStyle(projectAccent)}
    >
      <div className="site-footer__shell">
        <div className="site-footer__grid">
          <div className="site-footer__col">
            <span className="site-footer__label">Get in touch</span>
            <button
              type="button"
              className={`site-footer__email${justCopied ? ' is-copied' : ''}`}
              onClick={handleCopyEmail}
              aria-label={`Copy email address ${EMAIL}`}
            >
              <span className="site-footer__email-text">{EMAIL}</span>
              <svg
                className="site-footer__email-arrow"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4 12L12 4M12 4H6.5M12 4V9.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="site-footer__tooltip" role="status">
                {tooltipText}
              </span>
            </button>
            <p className="site-footer__note">
              Currently taking on new work for {workWindow}.
              <br />
              Usually replies within a day.
            </p>
          </div>

          <nav className="site-footer__col" aria-label="Site">
            <span className="site-footer__label">Site</span>
            <ul className="site-footer__list">
              {SITE_LINKS.map((link) => (
                <li key={link.sectionId}>
                  <Link
                    to="/"
                    onClick={(e) => handleSectionNav(e, link.sectionId)}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav className="site-footer__col" aria-label="Elsewhere">
            <span className="site-footer__label">Elsewhere</span>
            <ul className="site-footer__list site-footer__list--social">
              <li>
                <a
                  href="https://github.com/Kevin-Fuller"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                  </svg>
                  GitHub
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/in/kevin-fuller1/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                  LinkedIn
                </a>
              </li>
              <li>
                <Link to="/resume">
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M7 3.75h6.5L17.75 8v12.25H7V3.75Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.25 3.75V8h4.5M9.5 12h5M9.5 15.5h5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  Résumé
                </Link>
              </li>
            </ul>
          </nav>
        </div>

        <div className="site-footer__meta">
          <div className="site-footer__brand">
            <Link to="/" className="site-footer__mark" aria-label="Kevin Fuller, home">
              KF
            </Link>
            <p>
              Kevin Fuller <span aria-hidden="true">—</span> design &amp; development for the web
            </p>
          </div>

          <div className="site-footer__status">
            <span className="site-footer__available">
              <span className="site-footer__dot" aria-hidden="true" />
              Available
            </span>
            <span>Indiana, USA</span>
            <span>&copy; {YEAR}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

